'use client';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useSocket } from '@/hooks/useSocket';
import { useClobPricesHistory } from '@/services/query/';

export interface PricePoint {
  t: number;
  p: number;
}

export type SocketStatus = 'idle' | 'loading' | 'live' | 'error';

interface UseLivePriceSocketProps {
  assetId: string | undefined;
  interval?: string;
}

export const useLivePriceSocket = ({ assetId, interval = '1d' }: UseLivePriceSocketProps) => {
  const [liveDataMap, setLiveDataMap] = useState<Record<string, PricePoint[]>>({});

  const {
    data: historyData,
    isLoading: isHistoryLoading,
    isError: isHistoryError,
    refetch,
  } = useClobPricesHistory(assetId || '', interval);

  const onMessage = useCallback(
    (msg: any) => {
      try {
        if (msg === 'PONG') return;

        const events = Array.isArray(msg) ? msg : [msg];
        events.forEach((e: any) => {
          // Normalize asset_id to string for reliable key matching
          const msgAssetId = e.asset_id ? String(e.asset_id) : null;

          if (
            msgAssetId &&
            (e.event_type === 'last_trade_price' || e.event_type === 'price_change')
          ) {
            const price = e.event_type === 'last_trade_price' ? e.price : e.changes?.[0]?.price;
            if (!price) return;

            const point = {
              t: Math.floor(Number(e.timestamp) / 1000),
              p: parseFloat(price),
            };

            setLiveDataMap((prev) => ({
              ...prev,
              [msgAssetId]: [...(prev[msgAssetId] || []), point],
            }));
          }
        });
      } catch (_) {
        // Ignore non-JSON or parsing errors
      }
    },
    [assetId]
  );

  // 2. Access shared socket from context
  const { isConnected, send } = useSocket('crypto', onMessage);

  // Merge history with live updates, ensuring no duplicate timestamps
  const data = useMemo(() => {
    const history = historyData?.history || [];
    const currentAssetIdStr = assetId ? String(assetId) : '';
    const updates = currentAssetIdStr ? liveDataMap[currentAssetIdStr] || [] : [];

    if (updates.length === 0) return history;

    // Filter out live updates that are already in history or are older than the last history point
    const lastHistoryTimestamp = history.length > 0 ? history[history.length - 1].t : 0;
    const filteredLive = updates.filter(
      (lp) => lp.t > lastHistoryTimestamp && !history.some((hp) => hp.t === lp.t)
    );

    return [...history, ...filteredLive];
  }, [historyData, liveDataMap, assetId]);

  const status: SocketStatus = isHistoryLoading ? 'loading' : isHistoryError ? 'error' : 'live';

  // Cleanup old live data periodically to prevent memory growth
  useEffect(() => {
    const cleanupInterval = setInterval(() => {
      setLiveDataMap((prev) => {
        const newState = { ...prev };
        Object.keys(newState).forEach((key) => {
          if (newState[key].length > 1000) {
            newState[key] = newState[key].slice(-1000);
          }
        });
        return newState;
      });
    }, 60 * 1000);

    return () => clearInterval(cleanupInterval);
  }, []);

  // Subscribe when assetId changes or reconnects
  useEffect(() => {
    if (!assetId || !isConnected) return;

    // Wait slightly for connection to stabilize before sub
    const subTimeout = setTimeout(() => {
      const subMsg = {
        assets_ids: [assetId],
        type: 'market',
        custom_feature_enabled: true,
      };
      send(subMsg);
    }, 500);

    return () => {
      clearTimeout(subTimeout);
    };
  }, [assetId, isConnected, send]);

  return { data, status, fetchHistory: refetch };
};
